import { CircularProgress, Grid } from '@mui/joy'
import { useCallback, useEffect, useState } from 'react'
import Lightbox from 'yet-another-react-lightbox'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import 'yet-another-react-lightbox/styles.css'
import { Media } from '~/shared/api'
import { Button } from '~/shared/ui/Buttons'
import { ButtonProps } from '~/shared/ui/Buttons/Button'
import { Img } from '~/shared/ui/Img'
import { GalleryImage } from './type'

type ImageSliderProps = {
  images: GalleryImage[]
  xs?: number
  showList?: boolean
  buttonTitle?: string
  buttonProps?: Partial<ButtonProps>
  isLoading?: boolean
}

export function Gallery({
  images,
  xs = 3,
  showList = true,
  buttonTitle,
  buttonProps,
  isLoading,
}: ImageSliderProps) {
  const [isSlidersLoading, toggleSlidersLoading] = useState<boolean>(true)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [sliders, setSliders] = useState<GalleryImage[]>()
  const changeSelectedIndex = (index: number) => setSelectedIndex(index)

  const isOpen = selectedIndex >= 0

  const handleMediaGet = useCallback(async () => {
    if (sliders?.length) return

    try {
      toggleSlidersLoading(true)
      const resultSliders = []

      for (const image of images) {
        const blob = await Media.getMedia(image.src)
        resultSliders.push({ ...image, src: URL.createObjectURL(blob) })
      }

      setSliders(resultSliders)
    } finally {
      toggleSlidersLoading(false)
    }
  }, [images, sliders?.length])

  useEffect(() => {
    if (isOpen) {
      handleMediaGet()
    }
    // eslint-disable-next-line
  }, [isOpen])

  return (
    <>
      {showList && (
        <Grid container spacing={2.5} alignItems='center'>
          {images.map((item, index) => (
            <Grid
              xs={xs}
              sx={{
                cursor: 'pointer',
                transition: '0.15s ease opacity',

                '&:hover': {
                  opacity: '0.5',
                },
              }}
              key={item.id}
              onClick={() => changeSelectedIndex(index)}
            >
              <Img url={item.preview} />
            </Grid>
          ))}
        </Grid>
      )}
      {buttonTitle && (
        <Button
          variant='gray'
          {...buttonProps}
          onClick={() => changeSelectedIndex(0)}
          loading={isLoading}
        >
          {buttonTitle}
        </Button>
      )}
      <Lightbox
        carousel={{
          finite: images.length === 1,
        }}
        render={{
          ...(isSlidersLoading
            ? { slide: () => <CircularProgress size='sm' /> }
            : {}),
        }}
        slides={sliders || images}
        open={isOpen}
        index={selectedIndex}
        close={() => changeSelectedIndex(-1)}
        plugins={[Zoom]}
        animation={{ zoom: 300 }}
        zoom={{ maxZoomPixelRatio: 10, scrollToZoom: true }}
      />
    </>
  )
}
